import { useEffect, useState, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import { ArrowLeft, Loader2, Activity, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiKeysApi } from "@/lib/account";
import UserTokenUsageSection from "@/components/UserTokenUsageSection";

const CHART_GRID = "#44403c";
const CHART_TEXT = "#a8a29e";
const CHART_ACCENT = "#d97757";

/**
 * Fill in zero-count days so the chart shows a clean continuous timeline,
 * not just sparse dots when usage is light.
 */
function densify(daily, days) {
  const map = new Map(daily.map((d) => [d.date, d.count]));
  const out = [];
  // Build the timeline in UTC to match the server's %Y-%m-%d bucket exactly.
  const now = new Date();
  const todayUtc = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate()
  );
  const DAY_MS = 86_400_000;
  for (let i = days - 1; i >= 0; i--) {
    const key = new Date(todayUtc - i * DAY_MS).toISOString().slice(0, 10);
    out.push({ date: key.slice(5), count: map.get(key) || 0 });
  }
  return out;
}

function Tile({ label, value, sublabel, testid }) {
  return (
    <div data-testid={testid} className="border border-[var(--border)] px-5 py-5">
      <div className="font-mono-ui text-[9px] tracking-[0.32em] uppercase text-[var(--text-dim)]">
        {label}
      </div>
      <div className="font-serif text-3xl text-[var(--text)] mt-2 leading-none">{value}</div>
      {sublabel && (
        <div className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-muted)] mt-3">
          {sublabel}
        </div>
      )}
    </div>
  );
}

export default function AccountUsage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [windowDays, setWindowDays] = useState(30);

  const refresh = (days = windowDays) =>
    apiKeysApi
      .usage({ days })
      .then(setData)
      .catch((e) => toast.error(e.message || "Failed to load usage"));

  useEffect(() => {
    // Only load API-key call metrics if this user actually has API keys.
    // Non-API-key users still see this page — they just see the chat
    // token usage section below (which loads independently).
    if (user && typeof user === "object" && user.api_keys_enabled) {
      refresh(windowDays);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, windowDays]);

  const dense = useMemo(
    () => (data ? densify(data.daily, windowDays) : []),
    [data, windowDays]
  );
  const perKeyBar = useMemo(() => {
    if (!data) return [];
    return data.keys
      .filter((k) => k.tokens_used_this_month > 0 || (k.last_7d || []).length > 0)
      .map((k) => ({
        name: k.name.length > 14 ? k.name.slice(0, 14) + "…" : k.name,
        count: (k.last_7d || []).reduce((a, b) => a + b.count, 0),
      }));
  }, [data]);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] text-[var(--text-muted)] font-mono-ui text-[11px] tracking-[0.3em] uppercase">
        Loading…
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" state={{ from: "/account/usage" }} replace />;

  // Usage analytics are an operator-only surface. Regular users and
  // future paid users never see token counts, per-engine breakdowns,
  // or API-key call metrics on their own account. Admins only.
  const isAdmin = user?.role === "admin";
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
        <header className="border-b border-[var(--border)] px-6 md:px-10 py-5 flex items-center justify-between">
          <Link
            to="/"
            data-testid="usage-back"
            className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] inline-flex items-center gap-2 transition-colors"
          >
            <ArrowLeft size={13} /> Back to chat
          </Link>
          <h1 className="font-mono-ui text-[11px] tracking-[0.4em] uppercase">
            Usage
          </h1>
          <span />
        </header>
        <main className="max-w-2xl mx-auto px-6 md:px-10 py-16">
          <p
            data-testid="usage-not-available"
            className="font-serif italic text-2xl leading-tight text-[var(--text)]"
          >
            No usage details for now.
          </p>
          <p className="font-sans text-sm text-[var(--text-muted)] leading-relaxed mt-4">
            Humane Behavior AI is free for all signed-in users during
            beta. There&apos;s nothing to meter, so there&apos;s
            nothing to show here yet. Paid tiers, if introduced, will
            bring their own billing view.
          </p>
        </main>
      </div>
    );
  }

  const showApiKeysSection = !!user?.api_keys_enabled;

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] px-6 md:px-10 py-5 flex items-center justify-between">
        <Link
          to="/"
          data-testid="usage-back"
          className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] inline-flex items-center gap-2 transition-colors"
        >
          <ArrowLeft size={13} /> Back to chat
        </Link>
        <h1 className="font-mono-ui text-[11px] tracking-[0.4em] uppercase">
          API Usage
        </h1>
        {showApiKeysSection && (
        <Link
          to="/account/api-keys"
          data-testid="usage-to-keys"
          className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--accent)] hover:underline"
        >
          Manage keys →
        </Link>
        )}
        {!showApiKeysSection && <span />}
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 pb-20">
        {/* Chat token usage — always shown for logged-in users. */}
        <section className="pt-10 pb-8">
          <TrendingUp size={28} className="text-[var(--accent)] mb-5" />
          <h2 className="font-serif text-3xl sm:text-4xl text-[var(--text)] leading-tight max-w-2xl mb-3">
            Your chat token usage.
          </h2>
          <p className="font-sans text-sm text-[var(--text-muted)] max-w-xl leading-relaxed mb-6">
            Every message you send + reply you receive is counted here. Broken
            down by engine (DeepSeek, Sarvam, Gemini, Claude). No cost shown —
            usage is free for logged-in users.
          </p>
          <UserTokenUsageSection />
        </section>

        {showApiKeysSection && (
        <section className="py-12 border-t border-[var(--border)]">
          <Activity size={28} className="text-[var(--accent)] mb-5" />
          <h2 className="font-serif text-3xl sm:text-4xl text-[var(--text)] leading-tight max-w-2xl mb-3">
            API key usage.
          </h2>
          <p className="font-sans text-sm text-[var(--text-muted)] max-w-xl leading-relaxed">
            Calls counted against each key. Raw events kept for 90 days; the
            month counter resets on the 1st (UTC).
          </p>
        </section>
        )}

        {showApiKeysSection && !data && (
          <div className="py-24 flex items-center justify-center gap-3 text-[var(--text-muted)] font-mono-ui text-[11px] tracking-[0.3em] uppercase">
            <Loader2 className="animate-spin" size={14} /> Loading usage…
          </div>
        )}

        {showApiKeysSection && data && (
          <>
            {/* Window picker */}
            <div className="flex items-center gap-2 mt-8 mb-6">
              <span className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-muted)] mr-2">
                Window
              </span>
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setWindowDays(d)}
                  data-testid={`window-${d}`}
                  className={`font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border transition-colors ${
                    windowDays === d
                      ? "border-[var(--accent)] text-[var(--accent)]"
                      : "border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>

            {/* Top tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <Tile
                label="Calls in window"
                value={data.total_in_window.toLocaleString()}
                sublabel={`last ${windowDays} days`}
                testid="tile-window-total"
              />
              <Tile
                label="This month"
                value={data.month_used.toLocaleString()}
                sublabel={
                  data.month_cap === null
                    ? "unlimited"
                    : `of ${data.month_cap.toLocaleString()}`
                }
                testid="tile-month-used"
              />
              <Tile
                label="Active keys"
                value={data.keys.filter((k) => k.active).length}
                sublabel={`${data.keys.length} total`}
                testid="tile-active-keys"
              />
              <Tile
                label="Avg / day"
                value={(data.total_in_window / windowDays).toFixed(1)}
                sublabel={`over ${windowDays}d`}
                testid="tile-avg-day"
              />
            </div>

            {/* Time-series chart */}
            <div className="border border-[var(--border)] p-5 mb-8" data-testid="chart-daily">
              <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--text-muted)] mb-4">
                Calls by day · {windowDays}d
              </div>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <AreaChart data={dense} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="usageFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART_ACCENT} stopOpacity={0.4} />
                        <stop offset="100%" stopColor={CHART_ACCENT} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={CHART_GRID} strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="date" stroke={CHART_TEXT} fontSize={10} tickLine={false} axisLine={{ stroke: CHART_GRID }} />
                    <YAxis stroke={CHART_TEXT} fontSize={10} tickLine={false} axisLine={{ stroke: CHART_GRID }} width={32} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        background: "#1c1917",
                        border: "1px solid #44403c",
                        borderRadius: 0,
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 11,
                      }}
                    />
                    <Area type="monotone" dataKey="count" stroke={CHART_ACCENT} strokeWidth={1.5} fill="url(#usageFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Per-key breakdown */}
            <div className="grid lg:grid-cols-2 gap-4 mb-10">
              <div className="border border-[var(--border)] p-5" data-testid="chart-per-key">
                <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--text-muted)] mb-4">
                  Last 7 days by key
                </div>
                {perKeyBar.length === 0 ? (
                  <p className="font-sans text-sm text-[var(--text-dim)] py-10 text-center">
                    No calls in the last 7 days.
                  </p>
                ) : (
                  <div style={{ width: "100%", height: 220 }}>
                    <ResponsiveContainer>
                      <BarChart data={perKeyBar} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke={CHART_GRID} strokeDasharray="2 4" vertical={false} />
                        <XAxis dataKey="name" stroke={CHART_TEXT} fontSize={10} tickLine={false} axisLine={{ stroke: CHART_GRID }} />
                        <YAxis stroke={CHART_TEXT} fontSize={10} tickLine={false} axisLine={{ stroke: CHART_GRID }} width={32} allowDecimals={false} />
                        <Tooltip
                          contentStyle={{
                            background: "#1c1917",
                            border: "1px solid #44403c",
                            borderRadius: 0,
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 11,
                          }}
                        />
                        <Bar dataKey="count" fill={CHART_ACCENT} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              <div className="border border-[var(--border)] p-5" data-testid="key-table">
                <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--text-muted)] mb-4">
                  Per-key budget
                </div>
                {data.keys.length === 0 ? (
                  <p className="font-sans text-sm text-[var(--text-dim)] py-10 text-center">
                    No keys yet —{" "}
                    <Link to="/account/api-keys" className="text-[var(--accent)] hover:underline">
                      create one
                    </Link>
                    .
                  </p>
                ) : (
                  <div className="space-y-3">
                    {data.keys.map((k) => {
                      const used = k.tokens_used_this_month;
                      const cap = k.monthly_token_cap;
                      const pct = cap ? Math.min(100, (used / cap) * 100) : null;
                      return (
                        <div
                          key={k.id}
                          data-testid={`key-budget-${k.id}`}
                          className="border-t border-[var(--border-soft)] pt-3 first:border-t-0 first:pt-0"
                        >
                          <div className="flex items-baseline justify-between">
                            <div className="font-ui text-sm text-[var(--text)] truncate">
                              {k.name}
                            </div>
                            <div className="font-mono-ui text-[10px] tracking-[0.18em] text-[var(--text-muted)] ml-3">
                              {cap === null ? "∞" : `${used.toLocaleString()} / ${cap.toLocaleString()}`}
                            </div>
                          </div>
                          {pct !== null && (
                            <div className="h-1 bg-[var(--border-soft)] mt-2">
                              <div
                                className="h-1 bg-[var(--accent)] transition-all"
                                style={{ width: `${pct}%` }}
                                data-testid={`key-budget-bar-${k.id}`}
                              />
                            </div>
                          )}
                          <div className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-1.5">
                            {k.key_prefix}… · {k.requests_per_minute} rpm
                            {!k.active && " · revoked"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
